import { Injectable, signal, computed, effect } from '@angular/core';
import { ShiftID, GroupID, DashboardData } from '@app/models/dashboard.models';

const STORAGE_KEY = 'dashboard-storage';

@Injectable({ providedIn: 'root' })
export class DashboardStateService {
  readonly currentShift = signal<ShiftID>('A');
  readonly currentGroup = signal<GroupID>('B');
  readonly lastData = signal<DashboardData | null>(null);
  readonly isShiftChanging = signal<boolean>(false);
  readonly lastUpdated = signal<Date | null>(null);

  readonly hasData = computed(() => this.lastData() !== null);

  constructor() {
    this.restoreState();

    effect(() => {
      const state = {
        currentShift: this.currentShift(),
        currentGroup: this.currentGroup(),
        lastData: this.lastData(),
        lastUpdated: this.lastUpdated()?.toISOString() ?? null,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    });
  }

  setDashboardData(data: DashboardData, shift: ShiftID): void {
    const previousShift = this.currentShift();
    if (shift !== previousShift) {
      this.isShiftChanging.set(true);
      this.currentShift.set(shift);
    }
    this.lastData.set(data);
    this.lastUpdated.set(new Date());
  }

  completeShiftChange(): void {
    this.isShiftChanging.set(false);
  }

  private restoreState(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.currentShift) this.currentShift.set(parsed.currentShift);
        if (parsed.currentGroup) this.currentGroup.set(parsed.currentGroup);
        if (parsed.lastData) this.lastData.set(parsed.lastData);
        if (parsed.lastUpdated) this.lastUpdated.set(new Date(parsed.lastUpdated));
      }
    } catch {
      // Ignore malformed storage
    }
  }
}
